import React, { Component } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isReloading: false };
  }

  static getDerivedStateFromError(error) {
    // Check if the error is a ChunkLoadError
    const isChunkError = error.name === 'ChunkLoadError' || 
                         error.message?.includes('Loading chunk') ||
                         error.message?.includes('missing');
    
    return { hasError: true, error, isChunkError };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error', error, errorInfo);

    if (this.state.isChunkError) {
      // Use sessionStorage to prevent an infinite reload loop if the server is actually broken
      const storageKey = 'app_chunk_reload_attempt';
      const hasReloaded = sessionStorage.getItem(storageKey);

      if (!hasReloaded) {
        console.log('Chunk load error detected. Forcing reload to fetch new version...');
        sessionStorage.setItem(storageKey, 'true');
        this.setState({ isReloading: true });
        // Force reload from server, bypassing cache
        window.location.reload(true);
      } else {
        // If we already reloaded and it still fails, clear flag so they can try manually
        sessionStorage.removeItem(storageKey);
      }
    }
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null, isChunkError: false });
    window.location.reload();
  };

  render() {
    // Show a loading spinner while the page reloads automatically
    if (this.state.isReloading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Updating application...
          </Typography>
        </Box>
      );
    }

    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body1" sx={{ mb: 4 }}>
            {this.state.isChunkError 
              ? 'A new version of the app is available. Please refresh.' 
              : (this.state.error?.message || 'An unexpected error occurred')}
          </Typography>
          <Button variant="contained" onClick={this.resetErrorBoundary}>
            Reload Application
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;